import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import XLRLogger from 'src/common/helpers/logger';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { EPSVoidData } from 'src/common/interfaces/dtos/ITenderCancel';
import { TenderMenuActions } from 'src/common/redux/reducers/tender-menu-reducer';
import { PaymentMessage } from 'src/devices/payment/dtos/eps-data';
import {
  beginPaymentSession,
  inPaymentSession,
  voidPayment,
  cancelPinpadOperation,
  endPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import {
  CartPaymentData,
  CartTenders,
  TenderEPSData,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { buildReceipts } from 'src/tendering/business-logic/build-receipt-lines';
import { isEmeraldSellingService } from 'src/xlr-configuration/pos-service-type-utils';
import { ITenderAdd } from '../../common/interfaces/dtos/IPreTenderAdd';
import { store } from '../../common/redux/configure-store';
import {
  ITenderCancelDto,
  lineRefundCancel,
  lineTenderCancel,
} from '../../home-page/business-logic/service/line-tender-cancel';
import { applyCartResponse } from './common/apply-cart-response';
import { getPaymentOptions } from './get-payment-options';
import { preMakeTenderPayment } from './pre-make-tender-payment';
import { getCartRefunds } from 'src/tendering/business-logic/services/get-cart-refunds';

export const cancelTenderPayment = async (
  lineSequenceNumber: number,
  isRefund?: boolean,
  isEftVoided?: boolean,
): Promise<void> => {
  let epsData: EPSVoidData | undefined;
  let refundLineObj: ITenderCancelDto = { transactionSequenceNumber: 0 };
  const itemList = store.getState().itemList;
  const tenderLine = itemList.ejItems.find(
    x =>
      x.SequenceNumber === lineSequenceNumber &&
      (x.Type === EJRecordTypes.Tender || x.Type === EJRecordTypes.Refund),
  );
  const payment: CartPaymentData =
    itemList.payments?.find(x => x.sequenceNumber === lineSequenceNumber) || {};
  const isCashBackPayment = payment?.cashBackAmount && payment?.cashBackAmount > 0;

  const preTender = store.getState().tenderMenu.unPaidPreTender;

  const paymentTransactionId =
    preTender?.epsData?.authorization?.referenceNumber ?? tenderLine?.EpsData?.paymentTranId;

  if (paymentTransactionId && !isEftVoided) {
    try {
      await withSpinner(async () => {
        if (inPaymentSession()) {
          await cancelPinpadOperation();
          await endPaymentSession();
        }
        if (!inPaymentSession()) {
          await beginPaymentSession();
        }

        const paymentSessionId = store.getState().pinPad.sessionTransactionId ?? '';

        const payment = await voidPayment(paymentTransactionId, paymentSessionId);
        epsData = getEpsVoidData(payment, tenderLine?.EpsData);

        await endPaymentSession();
        await beginPaymentSession();
      });
    } catch (err) {
      const error: IMessageModal = {
        message: err?.message ?? 'AnErrorOccurred',
      };
      throw error;
    }
  }
  const transactionState = store.getState().transaction;
  const transactionId = transactionState.transactionId;
  const transactionSequenceNumber = transactionState.sequenceNumber;

  if (transactionId) {
    const unPaidPreTender = store.getState().tenderMenu.unPaidPreTender;
    if (unPaidPreTender) {
      await cancelPreTender(unPaidPreTender).catch(e => {
        XLRLogger.error({
          message: '',
          data: e,
          source: 'cancel-tender-payments.ts',
        });
      });
    }

    const lineTenderToCancel: ITenderCancelDto = {
      transactionSequenceNumber,
      isProcessedByEps: !!tenderLine?.EpsData,
      tenderSequenceNumber: lineSequenceNumber,
      epsData: epsData,
    };

    if (isRefund) {
      await lineRefundCancel(lineTenderToCancel, transactionId).then(applyCartResponse);
    } else {
      await lineTenderCancel(lineTenderToCancel, transactionId).then(applyCartResponse);
    }
    if (isCashBackPayment) {
      await withSpinner(async () => {
        const refunds = (await getCartRefunds(transactionId)) || [];
        const refundTender = getRefundForVoidedPayment(refunds, payment);
        const lineId = refundTender?.lineId;
        if (lineId) {
          refundLineObj = {
            transactionSequenceNumber,
            isProcessedByEps: !!tenderLine?.EpsData,
            tenderSequenceNumber: +lineId,
            epsData: epsData,
          };
          await lineRefundCancel(refundLineObj, transactionId).then(applyCartResponse);
        }
      });
    }
    await updateTenderMenu();
  }
};

const updateTenderMenu = async (): Promise<void> => {
  await getPaymentOptions();
};

const cancelPreTender = async (unPaidPreTender: ITenderAdd): Promise<void> => {
  if (unPaidPreTender.tender) {
    unPaidPreTender.tender.cancel = true;
  }
  if (isEmeraldSellingService()) {
    store.dispatch(TenderMenuActions.clearUnPaidPreTender());
    return;
  }
  await preMakeTenderPayment(unPaidPreTender);
};

const getEpsVoidData = (
  payment: PaymentMessage,
  epsTenderData?: TenderEPSData,
): EPSVoidData | undefined => {
  const epsData: EPSVoidData | undefined = epsTenderData
    ? {
        amountDue: epsTenderData.amountDue,
        tenderId: epsTenderData.tenderId,
        paymentSessionId: epsTenderData.paymentSessionId,
        sessionTranID: epsTenderData.paymentTranId,
        authorization: {
          authorizationCode: epsTenderData.authCode,
          referenceNumber: epsTenderData.referenceId,
        },
      }
    : undefined;

  if (epsData && payment.receipts) {
    epsData.card = {};
    buildReceipts(epsData.card, payment.receipts);
  }

  return epsData;
};

export const cancelUnPaidPreTender = async (isRefund = false): Promise<void> => {
  const { unPaidPreTender } = store.getState().tenderMenu;
  const { transactionId, sequenceNumber: transactionSequenceNumber } = store.getState().transaction;
  if (unPaidPreTender?.lineId) {
    return cancelPreTender(unPaidPreTender)
      .then(() => {
        if (isEmeraldSellingService()) {
          const tenderSequenceNumber = unPaidPreTender.lineId;
          const cancelCommand = isRefund ? lineRefundCancel : lineTenderCancel;
          return cancelCommand(
            { transactionSequenceNumber, tenderSequenceNumber },
            transactionId,
          ).then(applyCartResponse);
        }
      })
      .catch(() => {});
  }
};

export const getRefundForVoidedPayment = (refunds: CartTenders, payment: CartPaymentData) => {
  return refunds.pageContent.find(refund => {
    return refund.authorization?.paymentsInfo?.find(
      item => item.key === 'tenderSequence' && item.value === payment.sequence,
    );
  });
};
